import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import AddressForm from '../components/AddressForm';
import GovernmentIdForm from '../components/GovernmentIdForm';
import UserLookup from '../components/UserLookup';
import ConfirmModal from '../../components/ConfirmModal';
import {api} from '../../api';


export default function EditEmployee() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [addressTypes, setAddressTypes] = useState([]);
  const [governmentIdTypes, setGovernmentIdTypes] = useState([]);
  const [unmapping, setUnmapping] = useState(false);
  const [showUnmapModal, setShowUnmapModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    api(`/hcm/employee-address-types`).then(r => r.json()).then(setAddressTypes).catch(() => {});
    api(`/hcm/employee-government-id-types`).then(r => r.json()).then(setGovernmentIdTypes).catch(() => {});
    api(`/hcm/employees`).then(r => r.ok ? r.json() : []).then(setEmployees).catch(() => {});
    api(`/hcm/departments`).then(r => r.ok ? r.json() : []).then(setDepartments).catch(() => {});
    api(`/hcm/employees/${id}`).then(r => r.json()).then(e =>
      setForm({
        firstName: e.firstName || '', lastName: e.lastName || '', middleName: e.middleName || '',
        title: e.title || '', suffix: e.suffix || '', dateOfBirth: e.dateOfBirth || '',
        employeeId: e.employeeId || '', userId: e.userId || '', displayMiddleName: e.displayMiddleName || false,
        hireDate: e.hireDate || '', terminationDate: e.terminationDate || '', status: e.status || 'active',
        department: e.department || '', jobTitle: e.jobTitle || '', managerId: e.managerId || '',
        employmentType: e.employmentType || '', payRate: e.payRate || '', payType: e.payType || '', payFrequency: e.payFrequency || '',
        workEmail: e.workEmail || '', workPhone: e.workPhone || '',
        addresses: (e.addresses || []).map(a => ({ typeIds: (a.typeIds || []), line1: a.line1 || '', line2: a.line2 || '', line3: a.line3 || '', city: a.city || '', state: a.state || '', zip: a.zip || '', country: a.country || 'US' })),
        governmentIds: e.governmentIds || []
      })
    );
  }, [id]);

  if (!form) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { ...form, dateOfBirth: form.dateOfBirth || null, displayMiddleName: form.displayMiddleName,
      hireDate: form.hireDate || null, terminationDate: form.terminationDate || null, managerId: form.managerId || null,
      payRate: form.payRate ? parseFloat(form.payRate) : null };
    const res = await api(`/hcm/employees/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (res.ok) {
      navigate(`/hcm/employees/${id}`);
    }
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleUnmapUser = async () => {
    setShowUnmapModal(false);
    setUnmapping(true);
    try {
      const body = { ...form, userId: null };
      const res = await api(`/hcm/employees/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (res.ok) {
        setForm({ ...form, userId: '' });
      }
    } catch (error) {
      console.error('Failed to unmap user:', error);
    }
    setUnmapping(false);
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900">Edit Employee</h1>
        </div>
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input name="title" value={form.title} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input name="firstName" required value={form.firstName} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
              <input name="middleName" value={form.middleName} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input name="lastName" required value={form.lastName} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Suffix</label>
              <input name="suffix" value={form.suffix} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="displayMiddleName" checked={form.displayMiddleName} onChange={(e) => setForm({ ...form, displayMiddleName: e.target.checked })} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
            <label htmlFor="displayMiddleName" className="text-sm font-medium text-gray-700">Display middle name</label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
              <input name="employeeId" value={form.employeeId} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 pt-4">Contact Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
              <input name="workEmail" type="email" value={form.workEmail} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Phone</label>
              <input name="workPhone" value={form.workPhone} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 pt-4">Employment Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select name="status" value={form.status} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600">
                <option value="active">Active</option>
                <option value="on_leave">On Leave</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
              <input name="hireDate" type="date" value={form.hireDate} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Termination Date</label>
              <input name="terminationDate" type="date" value={form.terminationDate} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select name="employmentType" value={form.employmentType} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600">
                <option value="">Select...</option>
                <option value="full_time">Full-Time</option>
                <option value="part_time">Part-Time</option>
                <option value="contractor">Contractor</option>
                <option value="intern">Intern</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
              <input name="jobTitle" value={form.jobTitle} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select name="department" value={form.department} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600">
                <option value="">Select...</option>
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
              <select name="managerId" value={form.managerId} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600">
                <option value="">None</option>
                {employees.filter(emp => emp.id !== id).map(emp => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
              </select>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 pt-4">Compensation</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate</label>
              <input name="payRate" type="number" step="0.01" value={form.payRate} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
              <select name="payType" value={form.payType} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600">
                <option value="">Select...</option>
                <option value="hourly">Hourly</option>
                <option value="salary">Salary</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Frequency</label>
              <select name="payFrequency" value={form.payFrequency} onChange={onChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600">
                <option value="">Select...</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-Weekly</option>
                <option value="semimonthly">Semi-Monthly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link to User Account</label>
            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <UserLookup 
                  value={form.userId} 
                  onChange={(userId) => setForm({ ...form, userId })} 
                />
              </div>
              {form.userId && (
                <button
                  type="button"
                  onClick={() => setShowUnmapModal(true)}
                  disabled={unmapping}
                  className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {unmapping ? 'Unmapping...' : 'Unmap'}
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">Link this employee to a user account to enable timeclock functionality</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Addresses</label>
            <AddressForm addresses={form.addresses} addressTypes={addressTypes} onChange={(addresses) => setForm(f => ({ ...f, addresses }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Government IDs</label>
            <GovernmentIdForm governmentIds={form.governmentIds} governmentIdTypes={governmentIdTypes} onChange={(governmentIds) => setForm(f => ({ ...f, governmentIds }))} employeeId={id} api={api} />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
            <button type="button" onClick={() => navigate(`/hcm/employees/${id}`)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      </div>
      <ConfirmModal
        open={showUnmapModal}
        title="Unmap User"
        message="Are you sure you want to unmap this user? They will lose access to timeclock functionality until they re-map themselves."
        onConfirm={handleUnmapUser}
        onCancel={() => setShowUnmapModal(false)}
      />
    </Layout>
  );
}
